import { useEffect, useRef, useState } from 'react';
import { parseApiError } from '@/lib/api-error';
import { ConnectPicker, type Candidate } from './connect-picker';
import { FacebookPagePicker } from '@/components/facebook-page-picker';
import type { FacebookPageOption } from '@/lib/facebook-connect';
import { reasonCopy } from './reason-copy';

const API_URL = import.meta.env.VITE_API_URL ?? '';

type View = 'pending' | 'picker' | 'page-picker' | 'error';

interface PickerState {
  stateId: string;
  candidates: Candidate[];
}

/** AIT-621: a Facebook link whose account holds several eligible Pages. */
interface PagePickerState {
  stateId: string;
  pages: FacebookPageOption[];
}

interface InitialCallbackState {
  view: View;
  errorMsg: string | null;
  code: string | null;
  state: string | null;
  oauthError: string | null;
}

// Synchronous decisions (missing params) computed during render via lazy
// useState initializer — same pattern as cli-callback.tsx's computeInitialState.
// Async work (the POST) is the only thing that stays in useEffect, so we
// don't trip react-hooks/set-state-in-effect.
function computeInitialCallbackState(): InitialCallbackState {
  const params = new URLSearchParams(window.location.search);
  const oauthError = params.get('error');
  const state = params.get('state');
  if (oauthError && state) {
    return { view: 'pending', errorMsg: null, code: null, state, oauthError };
  }
  const code = params.get('code');
  if (!code || !state) {
    return { view: 'error', errorMsg: 'This link is no longer active.', code: null, state: null, oauthError: null };
  }
  return { view: 'pending', errorMsg: null, code, state, oauthError: null };
}

/**
 * /connect/callback — public (account-less) OAuth return page for SaaS
 * onboarding links.
 *
 * Meta redirects the end-customer's browser here after the WhatsApp / Instagram
 * Embedded Signup flow. This page:
 *
 * 1. Parses `?code` + `?state` from the URL (synchronously, via lazy initializer).
 * 2. POSTs `POST /org/connect/finalize` with those params.
 * 3. On `status: 'success'` → full-page navigate to `result.redirectUrl`
 *    (the SaaS success page set by the org admin).
 * 4. On `status: 'selection_required'` → renders ConnectPicker so the user
 *    can choose which WhatsApp number to connect; on `status: 'pick_page'`
 *    (AIT-621 Facebook links) → renders the shared FacebookPagePicker and
 *    POSTs the chosen Page to `POST /org/connect/select`.
 * 5. On error → neutral failure message; no internal details leaked.
 *
 * No WorkOS session, no auth headers — entirely public.
 */
export function ConnectCallbackPage() {
  const initial = useState<InitialCallbackState>(computeInitialCallbackState)[0];
  const [view, setView] = useState<View>(initial.view);
  const [errorMsg, setErrorMsg] = useState<string | null>(initial.errorMsg);
  const [picker, setPicker] = useState<PickerState | null>(null);
  const [pagePicker, setPagePicker] = useState<PagePickerState | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const called = useRef(false);

  const pickPage = async (page: FacebookPageOption) => {
    if (!pagePicker || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/org/connect/select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stateId: pagePicker.stateId, pageId: page.id }),
      });
      const data = (await res.json().catch(() => null)) as { redirectUrl?: string } | null;
      if (res.ok && data?.redirectUrl) {
        window.location.href = data.redirectUrl;
        return;
      }
      const apiError = parseApiError(res.status, data, 'Something went wrong. Please try the link again.');
      setErrorMsg(apiError.userMessage);
      setView('error');
    } catch {
      setErrorMsg('Connection error. Please try the link again.');
      setView('error');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (called.current) return;
    called.current = true;

    if (initial.view !== 'pending' || !initial.state) return;
    if (!initial.oauthError && !initial.code) return;

    void (async () => {
      if (initial.oauthError && initial.state) {
        try {
          const res = await fetch(`${API_URL}/org/connect/report-failure`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              state: initial.state,
              error: initial.oauthError,
              errorReason: new URLSearchParams(window.location.search).get('error_reason') ?? undefined,
            }),
          });
          const data = res.ok ? ((await res.json()) as { redirectUrl?: string }) : null;
          if (data?.redirectUrl) {
            window.location.href = data.redirectUrl;
            return;
          }
        } catch {
          // fall through to local copy — reporting must never strand the customer
        }
        // Same cancel rule as the backend: access_denied/user_denied = cancel,
        // anything else = transient.
        const params = new URLSearchParams(window.location.search);
        const isCancel =
          initial.oauthError === 'access_denied' || params.get('error_reason') === 'user_denied';
        setErrorMsg(reasonCopy(isCancel ? 'customer_cancelled' : 'temporary_failure'));
        setView('error');
        return;
      }

      const code = initial.code as string;
      const state = initial.state as string;

      try {
        const res = await fetch(`${API_URL}/org/connect/finalize`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, state }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => null);
          const apiError = parseApiError(
            res.status,
            data,
            'This link is no longer active.',
          );
          setErrorMsg(apiError.userMessage);
          setView('error');
          return;
        }

        const data = (await res.json()) as
          | { status: 'success'; redirectUrl: string }
          | { status: 'selection_required'; stateId: string; candidates: Candidate[] }
          | { status: 'pick_page'; stateId: string; pages: FacebookPageOption[] }
          | { status: 'failed'; reason: string; redirectUrl: string };

        if (data.status === 'success') {
          window.location.href = data.redirectUrl;
          return;
        }

        if (data.status === 'selection_required') {
          setPicker({ stateId: data.stateId, candidates: data.candidates });
          setView('picker');
          return;
        }

        if (data.status === 'pick_page') {
          setPagePicker({ stateId: data.stateId, pages: data.pages });
          setView('page-picker');
          return;
        }

        if (data.status === 'failed') {
          window.location.href = data.redirectUrl;
          return;
        }

        setErrorMsg('This link is no longer active.');
        setView('error');
      } catch {
        setErrorMsg('Connection error. Please try the link again.');
        setView('error');
      }
    })();
  }, [initial]);

  const center: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    fontFamily: 'system-ui',
    padding: 24,
    textAlign: 'center',
  };

  if (view === 'error') {
    return (
      <div style={center}>
        <p role="alert">{errorMsg}</p>
      </div>
    );
  }

  if (view === 'picker' && picker) {
    return (
      <div style={center}>
        <ConnectPicker
          stateId={picker.stateId}
          candidates={picker.candidates}
          onError={(msg) => {
            setErrorMsg(msg);
            setView('error');
          }}
        />
      </div>
    );
  }

  if (view === 'page-picker' && pagePicker) {
    // Same card the Connect Channel modal draws, so a long Page list stays a
    // bounded, scrolling panel instead of a column that runs off the screen.
    return (
      <div style={center}>
        <div className="grid w-full max-w-md gap-4 rounded-xl bg-background p-4 text-left text-sm ring-1 ring-foreground/10">
          <div className="flex flex-col gap-2">
            <h1 className="font-heading text-base leading-none font-medium">Choose a Facebook Page</h1>
            <p className="text-sm text-muted-foreground">Pick the Page you would like to connect.</p>
          </div>
          <FacebookPagePicker
            step={submitting ? 'submitting' : 'picking'}
            pages={pagePicker.pages}
            onPick={(p) => void pickPage(p)}
          />
        </div>
      </div>
    );
  }

  // pending — finalize in-flight
  return (
    <div style={{ ...center, background: '#1C1E21', color: '#fff' }}>
      <p>Connecting your account...</p>
    </div>
  );
}
